import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

const usePagination = (reduxSelector, fetchAction, fetchParams = {}) => {
  const dispatch = useDispatch();
  const reduxData = useSelector(reduxSelector);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // More efficient data update
  useEffect(() => {
    if (reduxData?.data) {
      const page = reduxData.pagination?.page || 0;
      if (page === 0) {
        setData(reduxData.data);
      } else {
        setData((prevData) => {
          const existingIds = new Set(prevData.map((item) => item.id));
          const newItems = reduxData.data.filter(
            (item) => !existingIds.has(item.id)
          );
          return [...prevData, ...newItems];
        });
      }
    }
  }, [reduxData?.data]);

  // Update hasMore based on pagination info
  useEffect(() => {
    if (reduxData?.pagination) {
      setHasMore(
        reduxData.pagination.page < reduxData.pagination.totalPages - 1
      );
    }
  }, [reduxData?.pagination]);

  const handleLoadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);
      await dispatch(
        fetchAction({
          ...fetchParams,
          page: currentPage + 1,
        })
      );
      setCurrentPage((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  }, [dispatch, fetchAction, fetchParams, currentPage, loading, hasMore]);

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      setCurrentPage(0);
      await dispatch(fetchAction({ ...fetchParams, page: 0 }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, fetchAction, fetchParams]);

  return {
    data,
    loading,
    hasMore,
    currentPage,
    handleRefresh,
    handleLoadMore,
    setData,
  };
};

export default usePagination;
